import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { WorkflowVersionType } from 'src/slices/workflow-preview/types';

interface SelectedVersion {
  version: number;
  versionType?: WorkflowVersionType;
}

interface UseSelectedAutoflowVersionReturn {
  clearSelectedVersion: () => void;
  selectedVersion: SelectedVersion | null;
  setSelectedVersion: (
    version: number,
    versionType?: WorkflowVersionType,
  ) => void;
}

export const useSelectedAutoflowVersion =
  (): UseSelectedAutoflowVersionReturn => {
    const [searchParams, setSearchParams] = useSearchParams();

    const selectedVersion = useMemo((): SelectedVersion | null => {
      const versionParam = searchParams.get('version');
      if (!versionParam) {
        return null;
      }
      const version = Number(versionParam);
      if (isNaN(version)) {
        return null;
      }
      const versionType = searchParams.get('versionType') as
        | WorkflowVersionType
        | undefined;
      return { version, versionType: versionType || undefined };
    }, [searchParams]);

    const setSelectedVersion = useCallback(
      (version: number, versionType?: WorkflowVersionType) => {
        setSearchParams(
          prev => {
            prev.set('version', String(version));
            if (versionType) {
              prev.set('versionType', versionType);
            } else {
              prev.delete('versionType');
            }
            return prev;
          },
          { replace: true },
        );
      },
      [setSearchParams],
    );

    const clearSelectedVersion = useCallback(() => {
      setSearchParams(
        prev => {
          prev.delete('version');
          prev.delete('versionType');
          return prev;
        },
        { replace: true },
      );
    }, [setSearchParams]);

    return {
      clearSelectedVersion,
      selectedVersion,
      setSelectedVersion,
    };
  };
